import { AlertTriangle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { UserConfirmedFlatSchema } from 'shared';

import { useRecordUserConfirmedFlat } from '../../features/positions/mutations';
import type { PositionDto } from '../../lib/api/endpoints';
import { Button } from '../ui/Button';
import { FormField } from '../ui/FormField';

interface UserConfirmedFlatFormProps {
  position: PositionDto;
  onSuccess: () => void;
}

interface UserConfirmedFlatFormValues {
  observedAt: string;
  confirmation: boolean;
}

export function UserConfirmedFlatForm({ position, onSuccess }: UserConfirmedFlatFormProps) {
  const resolution = useRecordUserConfirmedFlat();
  const form = useForm<UserConfirmedFlatFormValues>({
    defaultValues: {
      observedAt: new Date().toISOString().slice(0, 10),
      confirmation: false,
    },
  });
  const onSubmit = form.handleSubmit((values) => {
    const body = UserConfirmedFlatSchema.parse(values);
    resolution.mutate(
      { accountId: position.accountId, symbol: position.symbol, body },
      { onSuccess },
    );
  });

  return (
    <form className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5" onSubmit={onSubmit}>
      <div className="flex gap-2 rounded border border-warn/70 bg-warn-subtle p-3 text-sm text-amber-100">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
        <p>
          This records your confirmation that you do not currently hold {position.symbol}.
          Executions and historical P&amp;L remain unresolved. Any new or changed execution
          invalidates this confirmation.
        </p>
      </div>
      <FormField
        label="Confirmation date"
        type="date"
        error={form.formState.errors.observedAt?.message}
        {...form.register('observedAt', { required: 'Confirmation date is required.' })}
      />
      <label className="flex items-start gap-3 rounded border border-border bg-bg-muted p-3 text-sm text-text">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-primary"
          {...form.register('confirmation', {
            required: 'You must confirm that this position is currently flat.',
          })}
        />
        <span>I confirm that I do not currently hold this position.</span>
      </label>
      {form.formState.errors.confirmation ? (
        <p className="text-sm text-danger">{form.formState.errors.confirmation.message}</p>
      ) : null}
      <p className="text-sm text-text-muted">Source: authenticated user</p>
      {resolution.isError ? (
        <p className="text-sm text-danger">Could not record your position confirmation.</p>
      ) : null}
      <div className="flex justify-end border-t border-border pt-4">
        <Button type="submit" disabled={resolution.isPending}>
          {resolution.isPending ? 'Recording...' : 'Confirm flat position'}
        </Button>
      </div>
    </form>
  );
}
